import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Check, MapPin, Settings as SettingsIcon, Building, Phone, Mail, Globe, Calculator, Upload, Image, Save, AlertCircle, Send, Calendar, TestTube, Loader2 } from 'lucide-react';
import { invalidateGeolocationConfigCache } from '../utils/geolocation';
import { calculerTVAIntracommunautaire, validerInformationsSociete } from '../services/companyInfoService';
import '../GeneralSettings.css';

// Composant de section (défini en dehors pour éviter la perte de focus)
const Section = ({ title, icon: Icon, children }) => (
  <div className="settings-section">
    <div className="settings-section-title">
      <div className="section-icon">
        <Icon size={20} />
      </div>
      <h3>{title}</h3>
    </div>
    {children}
  </div>
);

// Composant de champ (défini en dehors pour éviter la perte de focus)
const Field = ({ label, children, help, className = '' }) => (
  <div className={`settings-field ${className}`}>
    <label className="settings-label">{label}</label>
    {children}
    {help && <p className="settings-help">{help}</p>}
  </div>
);

const GeneralSettings = ({ db, onSettingsUpdate }) => {
  const [settings, setSettings] = useState({
    organizationName: '',
    timeZone: 'Europe/Paris',
    country: '',
    googleMapsApiKey: '',
    checkInRadius: 100,
    denominationSociale: '',
    sigle: '',
    formeJuridique: '',
    siren: '',
    siret: '',
    numeroSAP: '',
    capitalSocial: '',
    immatriculation: '',
    codeNafApe: '',
    siegeSocial: '',
    president: '',
    telephone: '',
    email: '',
    emailSecondaire: '',
    siteWeb: '',
    tvaIntracommunautaire: '',
    conditionsVente: '',
    mentionsLegales: '',
    logoUrl: '',
    useLogoInHeader: false,
    logoDisplayHeight: 32, // Hauteur en pixels par défaut
    clientPortalUrl: '', // URL du portail client
    // Configuration IMAP/SMTP
    emailProvider: 'imap',
    imapHost: '',
    imapPort: 993,
    imapUsername: '',
    imapPassword: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    emailFromName: '',
    emailFromAddress: '',
    emailUseSSL: true,
    emailUseTLS: true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [ongletActif, setOngletActif] = useState('general');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings(data);
      }
    };
    fetchSettings();
  }, [db]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => {
      const newSettings = {
        ...prev,
        [name]: value
      };

      if (name === 'siren' && value) {
        const tvaCalculee = calculerTVAIntracommunautaire(value);
        if (tvaCalculee && !prev.tvaIntracommunautaire) {
          newSettings.tvaIntracommunautaire = tvaCalculee;
        }
      }

      return newSettings;
    });
  };

  const calculerTVA = () => {
    if (settings.siren) {
      const tvaCalculee = calculerTVAIntracommunautaire(settings.siren);
      setSettings(prev => ({
        ...prev,
        tvaIntracommunautaire: tvaCalculee
      }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide (JPG, PNG, GIF, etc.)');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale: 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({
          ...prev,
          logoUrl: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setSettings(prev => ({
      ...prev,
      logoUrl: ''
    }));
  };

  const validerInformations = () => {
    const result = validerInformationsSociete(settings);
    setValidationResult(result);
    return result;
  };

  const saveSettings = async () => {
    const validation = validerInformations();

    if (!validation) {
      setValidationResult({ valide: false, erreurs: ['Erreur de validation'], avertissements: [] });
      return;
    }
    if (!validation.valide) {
      setValidationResult(validation);
      return;
    }

    setIsSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'general'), settings);
      setSaveSuccess(true);
      onSettingsUpdate(settings);
      invalidateGeolocationConfigCache();
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des paramètres:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const testerSMTP = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      // D'abord sauvegarder les paramètres pour s'assurer que la fonction utilise les derniers réglages
      await setDoc(doc(db, 'settings', 'general'), settings);

      const testSmtpFunction = httpsCallable(functions, 'testSmtpConfiguration');
      const response = await testSmtpFunction();
      setTestResult(response.data);
    } catch (error) {
      console.error("Erreur lors du test SMTP:", error);
      setTestResult({ success: false, message: error.message });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="general-settings-container">
      {/* En-tête */}
      <div className="general-settings-header">
        <div>
          <h1 className="general-settings-title">Paramètres généraux</h1>
          <p className="settings-help">Configuration de l'application et informations de la société</p>
        </div>
        <div className="settings-actions-content">
          {saveSuccess && (
            <div className="settings-success">
              <Check size={16} />
              Sauvegardé
            </div>
          )}
          <button
            onClick={saveSettings}
            disabled={isSaving}
            className="settings-button"
          >
            <Save size={16} />
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="settings-tabs-container">
        <nav className="settings-tabs-nav">
          {[
            { id: 'general', label: 'Général', icon: SettingsIcon },
            { id: 'societe', label: 'Société', icon: Building },
            { id: 'contact', label: 'Contact', icon: Phone },
            { id: 'comptabilite', label: 'Comptabilité', icon: Globe },
            { id: 'logo', label: 'Logo', icon: Image },
            { id: 'maps', label: 'Google Maps', icon: MapPin },
            { id: 'email', label: 'Email', icon: Send },
            { id: 'planning', label: 'Planning', icon: Calendar }
          ].map((onglet) => (
            <button
              key={onglet.id}
              onClick={() => setOngletActif(onglet.id)}
              className={`settings-tab-button ${ongletActif === onglet.id ? 'active' : ''}`}
            >
              <onglet.icon className="settings-tab-icon" />
              <span className="settings-tab-label">{onglet.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Affichage des résultats de validation */}
        {validationResult && !validationResult.valide && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="text-red-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-red-800 mb-2">Erreurs de validation</h4>
                {validationResult.erreurs && validationResult.erreurs.length > 0 && (
                  <ul className="text-red-700 text-sm list-disc list-inside">
                    {validationResult.erreurs.map((erreur, index) => (
                      <li key={index}>{erreur}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Onglet Général */}
        {ongletActif === 'general' && (
          <Section title="Configuration générale" icon={SettingsIcon}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Nom de l'organisation">
                <input
                  type="text"
                  name="organizationName"
                  value={settings.organizationName}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="Entrez le nom de votre organisation"
                />
              </Field>

              <Field label="Fuseau horaire">
                <select
                  name="timeZone"
                  value={settings.timeZone}
                  onChange={handleInputChange}
                  className="settings-select"
                >
                  <option value="Europe/Paris">Europe/Paris (France)</option>
                  <option value="Europe/Brussels">Europe/Brussels (Belgique)</option>
                  <option value="Europe/Zurich">Europe/Zurich (Suisse)</option>
                  <option value="Europe/Luxembourg">Europe/Luxembourg (Luxembourg)</option>
                </select>
              </Field>

              <Field label="Pays">
                <input
                  type="text"
                  name="country"
                  value={settings.country}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="Entrez le pays principal d'activité"
                />
              </Field>

              <Field
                label="URL du Portail Client"
                help="Adresse web où les bénéficiaires peuvent se connecter (ex: https://client.omadservices.com)"
              >
                <input
                  type="url"
                  name="clientPortalUrl"
                  value={settings.clientPortalUrl}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="https://client.votre-domaine.com"
                />
              </Field>

              <Field
                label="Rayon de pointage GPS (mètres)"
                help="Distance maximale autorisée entre l'employé et le client pour permettre le pointage GPS"
              >
                <input
                  type="number"
                  name="checkInRadius"
                  value={settings.checkInRadius}
                  onChange={handleInputChange}
                  min="10"
                  max="1000"
                  step="10"
                  className="settings-input"
                />
              </Field>
            </div>
          </Section>
        )}

        {/* Onglet Société */}
        {ongletActif === 'societe' && (
          <Section title="Informations de la société" icon={Building}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Dénomination sociale">
                <input
                  type="text"
                  name="denominationSociale"
                  value={settings.denominationSociale}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="OMAD SERVICES"
                />
              </Field>

              <Field label="Sigle">
                <input
                  type="text"
                  name="sigle"
                  value={settings.sigle}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="OMAD"
                />
              </Field>

              <Field label="Forme juridique">
                <input
                  type="text"
                  name="formeJuridique"
                  value={settings.formeJuridique}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="SAS (Société par Actions Simplifiée)"
                />
              </Field>

              <Field label="Capital social">
                <input
                  type="text"
                  name="capitalSocial"
                  value={settings.capitalSocial}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="500 €"
                />
              </Field>

              <Field label="SIREN">
                <input
                  type="text"
                  name="siren"
                  value={settings.siren}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="922 592 662"
                />
              </Field>

              <Field label="SIRET">
                <input
                  type="text"
                  name="siret"
                  value={settings.siret}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="922 592 662 00014"
                />
              </Field>

              <Field label="Numéro SAP">
                <input
                  type="text"
                  name="numeroSAP"
                  value={settings.numeroSAP}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="SAP922592662"
                />
              </Field>

              <Field label="Code NAF / APE">
                <input
                  type="text"
                  name="codeNafApe"
                  value={settings.codeNafApe}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="8810A – Aide à domicile"
                />
              </Field>

              <Field label="Immatriculation">
                <input
                  type="text"
                  name="immatriculation"
                  value={settings.immatriculation}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="RCS Bourges, le 17 octobre 2022"
                />
              </Field>

              <Field label="Président(e)" className="md:col-span-2">
                <input
                  type="text"
                  name="president"
                  value={settings.president}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="Mme Virginie Cahagne"
                />
              </Field>

              <Field label="Siège social" className="md:col-span-2">
                <textarea
                  name="siegeSocial"
                  value={settings.siegeSocial}
                  onChange={handleInputChange}
                  rows="3"
                  className="settings-textarea"
                  placeholder="30 bis Rue des Bondoires, 18230 Saint-Doulchard"
                />
              </Field>
            </div>
          </Section>
        )}

        {/* Onglet Contact */}
        {ongletActif === 'contact' && (
          <Section title="Coordonnées de contact" icon={Phone}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Téléphone">
                <input
                  type="tel"
                  name="telephone"
                  value={settings.telephone}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="06 67 72 73 30"
                />
              </Field>

              <Field label="Email principal">
                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="contact@omadservices.com"
                />
              </Field>

              <Field label="Email secondaire">
                <input
                  type="email"
                  name="emailSecondaire"
                  value={settings.emailSecondaire}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="contact@omadservices.com"
                />
              </Field>

              <Field label="Site web">
                <input
                  type="url"
                  name="siteWeb"
                  value={settings.siteWeb}
                  onChange={handleInputChange}
                  className="settings-input"
                  placeholder="www.omadservices.com"
                />
              </Field>
            </div>
          </Section>
        )}

        {/* Onglet Comptabilité */}
        {ongletActif === 'comptabilite' && (
          <Section title="Configuration comptable" icon={Globe}>
            <div className="space-y-6">
              <Field
                label="Numéro de TVA intracommunautaire"
                help="Format: FR + SIREN (calculé automatiquement si vide)"
              >
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="tvaIntracommunautaire"
                    value={settings.tvaIntracommunautaire}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="FRXX 922 592 662"
                  />
                  <button
                    type="button"
                    onClick={calculerTVA}
                    disabled={!settings.siren}
                    className="px-3 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                    title="Calculer automatiquement la TVA à partir du SIREN"
                  >
                    <Calculator size={16} />
                  </button>
                </div>
              </Field>

              <Field label="Conditions générales de vente">
                <textarea
                  name="conditionsVente"
                  value={settings.conditionsVente}
                  onChange={handleInputChange}
                  rows="6"
                  className="settings-textarea"
                  placeholder="Entrez vos conditions générales de vente..."
                />
              </Field>

              <Field label="Mentions légales">
                <textarea
                  name="mentionsLegales"
                  value={settings.mentionsLegales}
                  onChange={handleInputChange}
                  rows="6"
                  className="settings-textarea"
                  placeholder="Entrez vos mentions légales..."
                />
              </Field>
            </div>
          </Section>
        )}

        {/* Onglet Logo */}
        {ongletActif === 'logo' && (
          <Section title="Logo de l'entreprise" icon={Image}>
            <div className="space-y-6">
              {/* Option d'utilisation du logo dans le header */}
              <Field label="Utilisation du logo">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useLogoInHeader"
                      name="useLogoInHeader"
                      checked={settings.useLogoInHeader}
                      onChange={(e) => setSettings(prev => ({ ...prev, useLogoInHeader: e.target.checked }))}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="useLogoInHeader" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Utiliser le logo dans l'en-tête de l'application
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 ml-7">
                    {settings.useLogoInHeader
                      ? "Le logo remplacera le nom de l'organisation dans l'en-tête"
                      : "Le nom de l'organisation sera affiché dans l'en-tête"}
                  </p>
                </div>
              </Field>

              {/* Contrôle de la taille d'affichage du logo */}
              {settings.useLogoInHeader && (
                <Field
                  label="Taille d'affichage du logo"
                  help="Hauteur en pixels (la largeur s'ajuste automatiquement pour conserver les proportions)"
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        name="logoDisplayHeight"
                        value={settings.logoDisplayHeight}
                        onChange={handleInputChange}
                        min="16"
                        max="64"
                        step="4"
                        className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-12">
                        {settings.logoDisplayHeight}px
                      </span>
                    </div>
                    {settings.logoUrl && (
                      <div className="flex items-center justify-center p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                        <img
                          src={settings.logoUrl}
                          alt="Aperçu du logo"
                          style={{ height: `${settings.logoDisplayHeight}px`, width: 'auto' }}
                          className="object-contain"
                        />
                      </div>
                    )}
                  </div>
                </Field>
              )}

              {/* Aperçu du logo */}
              {settings.logoUrl && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <img
                    src={settings.logoUrl}
                    alt="Logo de l'entreprise"
                    className="w-16 h-16 object-contain border border-gray-200 dark:border-gray-600 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Aperçu du logo</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Le logo apparaîtra sur tous les documents</p>
                  </div>
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="px-3 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-800 text-sm transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              )}

              {/* Zone d'upload */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer block"
                >
                  <Upload className="mx-auto text-gray-400 dark:text-gray-500 mb-2" size={32} />
                  <p className="text-gray-600 dark:text-gray-400 font-medium">
                    {settings.logoUrl ? 'Changer le logo' : 'Ajouter un logo'}
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mt-1">
                    PNG, JPG, GIF - Max 2MB
                  </p>
                </label>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-500">
                Le logo sera affiché sur les devis, factures, contrats et autres documents
              </p>
            </div>
          </Section>
        )}

        {/* Onglet Google Maps */}
        {ongletActif === 'maps' && (
          <Section title="Configuration Google Maps" icon={MapPin}>
            <div className="space-y-6">
              <Field
                label="Clé API Google Maps"
                help="Clé nécessaire pour la géolocalisation et les calculs d'itinéraires"
              >
                <input
                  type="password"
                  name="googleMapsApiKey"
                  value={settings.googleMapsApiKey}
                  onChange={handleInputChange}
                  placeholder="AIzaSy..."
                  className="settings-input"
                />
              </Field>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Instructions</h4>
                <ul className="text-blue-700 dark:text-blue-400 text-sm list-disc list-inside space-y-1">
                  <li>Créez une clé API dans Google Cloud Console</li>
                  <li>Activez les APIs: Maps JavaScript, Geocoding, Directions</li>
                  <li>Restreignez la clé à votre domaine</li>
                  <li>Configurez des quotas pour éviter les dépassements</li>
                </ul>
              </div>
            </div>
          </Section>
        )}

        {/* Onglet Email */}
        {ongletActif === 'email' && (
          <Section title="Configuration des emails" icon={Send}>
            <div className="space-y-6">
              {/* Configuration générale */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Configuration générale</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Nom de l'expéditeur">
                    <input
                      type="text"
                      name="emailFromName"
                      value={settings.emailFromName}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="OMAD SERVICES"
                    />
                  </Field>

                  <Field label="Email de l'expéditeur">
                    <input
                      type="email"
                      name="emailFromAddress"
                      value={settings.emailFromAddress}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="noreply@omadservices.com"
                    />
                  </Field>
                </div>
              </div>

              {/* Configuration IMAP */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Configuration IMAP (Réception)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Serveur IMAP">
                    <input
                      type="text"
                      name="imapHost"
                      value={settings.imapHost}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="imap.gmail.com"
                    />
                  </Field>

                  <Field label="Port IMAP">
                    <input
                      type="number"
                      name="imapPort"
                      value={settings.imapPort}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="993"
                    />
                  </Field>

                  <Field label="Nom d'utilisateur IMAP">
                    <input
                      type="text"
                      name="imapUsername"
                      value={settings.imapUsername}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="votre-email@gmail.com"
                    />
                  </Field>

                  <Field label="Mot de passe IMAP">
                    <input
                      type="password"
                      name="imapPassword"
                      value={settings.imapPassword}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="Votre mot de passe"
                    />
                  </Field>
                </div>
              </div>

              {/* Configuration SMTP */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-green-800 dark:text-green-300 mb-3">Configuration SMTP (Envoi)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Serveur SMTP">
                    <input
                      type="text"
                      name="smtpHost"
                      value={settings.smtpHost}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="smtp.gmail.com"
                    />
                  </Field>

                  <Field label="Port SMTP">
                    <input
                      type="number"
                      name="smtpPort"
                      value={settings.smtpPort}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="587"
                    />
                  </Field>

                  <Field label="Nom d'utilisateur SMTP">
                    <input
                      type="text"
                      name="smtpUsername"
                      value={settings.smtpUsername}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="votre-email@gmail.com"
                    />
                  </Field>

                  <Field label="Mot de passe SMTP">
                    <input
                      type="password"
                      name="smtpPassword"
                      value={settings.smtpPassword}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="Votre mot de passe"
                    />
                  </Field>
                </div>
              </div>

              {/* Options de sécurité */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-3">Options de sécurité</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="emailUseSSL"
                        checked={settings.emailUseSSL}
                        onChange={(e) => setSettings(prev => ({ ...prev, emailUseSSL: e.target.checked }))}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Utiliser SSL</span>
                    </div>
                  </Field>

                  <Field label="">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="emailUseTLS"
                        checked={settings.emailUseTLS}
                        onChange={(e) => setSettings(prev => ({ ...prev, emailUseTLS: e.target.checked }))}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Utiliser TLS</span>
                    </div>
                  </Field>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Instructions de configuration</h4>
                <ul className="text-gray-700 dark:text-gray-300 text-sm list-disc list-inside space-y-1">
                  <li>Pour Gmail : Activez l'authentification à 2 facteurs et créez un mot de passe d'application</li>
                  <li>Pour Outlook : Utilisez votre mot de passe normal ou un mot de passe d'application</li>
                  <li>Les ports standards : IMAP (993), SMTP (587)</li>
                  <li>Testez la configuration avec l'outil de test ci-dessous</li>
                </ul>
              </div>

              {/* Bouton de test */}
              <div className="flex flex-col space-y-4">
                <button
                  type="button"
                  onClick={testerSMTP}
                  disabled={isTesting || !settings.smtpHost || !settings.smtpUsername || !settings.smtpPassword}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all shadow-sm ${isTesting
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
                    }`}
                >
                  {isTesting ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <TestTube size={20} />
                  )}
                  {isTesting ? 'Test en cours...' : 'Tester la configuration SMTP'}
                </button>

                {testResult && (
                  <div className={`p-4 rounded-xl border flex items-start gap-3 animate-in fade-in slide-in-from-top-2 ${testResult.success
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                    {testResult.success ? (
                      <Check size={20} className="mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <h5 className="font-bold">{testResult.success ? 'Succès !' : 'Erreur'}</h5>
                      <p className="text-sm opacity-90">{testResult.message}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* Onglet Planning */}
        {ongletActif === 'planning' && (
          <Section title="Configuration du planning" icon={Calendar}>
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Configuration des horaires d'affichage</h4>
                <p className="text-blue-700 dark:text-blue-400 text-sm mb-4">
                  Configurez les heures d'ouverture et de fermeture du planning pour l'affichage dans les vues jour, semaine et mois.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Heure d'ouverture">
                    <input
                      type="time"
                      name="planningOpeningTime"
                      value={settings.planningOpeningTime || '08:00'}
                      onChange={handleInputChange}
                      className="settings-input"
                    />
                  </Field>

                  <Field label="Heure de fermeture">
                    <input
                      type="time"
                      name="planningClosingTime"
                      value={settings.planningClosingTime || '18:00'}
                      onChange={handleInputChange}
                      className="settings-input"
                    />
                  </Field>
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-3">Options d'affichage</h4>

                <div className="space-y-4">
                  <Field label="">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="showWeekends"
                        checked={settings.showWeekends !== false}
                        onChange={(e) => setSettings(prev => ({ ...prev, showWeekends: e.target.checked }))}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Afficher les week-ends dans le planning</span>
                    </div>
                  </Field>

                  <Field label="Premier jour de la semaine">
                    <select
                      name="firstDayOfWeek"
                      value={settings.firstDayOfWeek || 1}
                      onChange={handleInputChange}
                      className="settings-select"
                    >
                      <option value={0}>Dimanche</option>
                      <option value={1}>Lundi</option>
                      <option value={2}>Mardi</option>
                      <option value={3}>Mercredi</option>
                      <option value={4}>Jeudi</option>
                      <option value={5}>Vendredi</option>
                      <option value={6}>Samedi</option>
                    </select>
                  </Field>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">Impact des paramètres</h4>
                <ul className="text-yellow-700 dark:text-yellow-400 text-sm list-disc list-inside space-y-1">
                  <li>Les heures d'ouverture/fermeture affectent l'affichage dans toutes les vues du planning</li>
                  <li>Le premier jour de la semaine affecte l'affichage des vues semaine et mois</li>
                </ul>
              </div>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default GeneralSettings;
